import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Languages, Loader2, RefreshCw, Check, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANGS = [
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
];

const CONTENT_TYPES = [
  { key: 'events', label: 'Events', desc: 'Event short & long descriptions' },
  { key: 'cities', label: 'City & Venue Intros', desc: 'City and venue intro paragraphs' },
  { key: 'blogs', label: 'Blog Posts', desc: 'Blog title, excerpt & content' },
  { key: 'reports', label: 'Insight Reports', desc: 'Report title & executive summary' },
];

export default function AdminTranslationsPage() {
  const token = localStorage.getItem('admin_token');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null); // contentType currently running
  const [message, setMessage] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/translations/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch (e) {
      console.error('Failed to load translation status', e);
      setMessage({ type: 'error', text: 'Failed to load translation status.' });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const runBatch = async (contentType) => {
    setRunning(contentType);
    setMessage(null);
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    try {
      // Run repeatedly until nothing remains (bounded batches keep each call responsive)
      let totalProcessed = 0;
      let guard = 0;
      // limit per call: smaller for long content types
      const limit = contentType === 'events' || contentType === 'cities' ? 10 : 3;
      while (guard < 100) {
        guard += 1;
        const res = await axios.post(
          `${API}/admin/translations/run`,
          { contentType, langs: LANGS.map((l) => l.code), limit },
          headers
        );
        totalProcessed += res.data.processed || 0;
        await fetchStatus();
        if (!res.data.remaining || res.data.processed === 0) break;
      }
      setMessage({ type: 'success', text: `Translated ${totalProcessed} ${contentType} item(s).` });
    } catch (e) {
      console.error('Translation run failed', e);
      const detail = e?.response?.data?.detail || 'Translation run failed.';
      setMessage({ type: 'error', text: detail });
    } finally {
      setRunning(null);
      fetchStatus();
    }
  };

  const pct = (done, total) => (total > 0 ? Math.round((done / total) * 100) : 0);

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="admin-translations-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Languages className="w-6 h-6 text-orange-600" /> Translations
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              AI-translate dynamic content (GPT-4o-mini) into German, French and Spanish. Idempotent — already-translated items are skipped.
            </p>
          </div>
          <Button variant="outline" onClick={fetchStatus} disabled={loading} data-testid="refresh-status-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {status && !status.llmConfigured && (
          <div className="flex items-center gap-2 p-4 rounded-lg bg-red-50 text-red-700 border border-red-200">
            <AlertCircle className="w-5 h-5" /> LLM key not configured — translation is unavailable.
          </div>
        )}

        {message && (
          <div
            className={`flex items-center gap-2 p-4 rounded-lg border ${
              message.type === 'success'
                ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-red-50 text-red-700 border-red-200'
            }`}
            data-testid="translation-message"
          >
            {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {CONTENT_TYPES.map((ct) => {
              const section = status?.[ct.key === 'cities' ? 'intros' : ct.key];
              const total = section?.total || 0;
              const done = section?.done || {};
              return (
                <Card key={ct.key} data-testid={`translation-card-${ct.key}`}>
                  <CardHeader>
                    <CardTitle className="text-lg">{ct.label}</CardTitle>
                    <CardDescription>{ct.desc}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      {LANGS.map((l) => (
                        <div key={l.code} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">{l.label}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 bg-gray-100 rounded-full h-2 overflow-hidden">
                              <div
                                className="bg-orange-500 h-2"
                                style={{ width: `${pct(done[l.code] || 0, total)}%` }}
                              />
                            </div>
                            <Badge variant="secondary" className="min-w-[64px] justify-center">
                              {done[l.code] || 0}/{total}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Button
                      className="w-full bg-orange-600 hover:bg-orange-700"
                      onClick={() => runBatch(ct.key)}
                      disabled={!!running || !status?.llmConfigured}
                      data-testid={`translate-${ct.key}-btn`}
                    >
                      {running === ct.key ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Translating…</>
                      ) : (
                        <>Translate {ct.label}</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
